import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useI18n } from '../../contexts/I18nContext';

interface LogoutButtonProps {
  className?: string;
  children?: React.ReactNode;
  showConfirmation?: boolean;
  onLogoutStart?: () => void;
  onLogoutComplete?: () => void;
}

/**
 * LogoutButton component that handles user logout with optional confirmation
 */
export function LogoutButton({
  className = '',
  children,
  showConfirmation = false,
  onLogoutStart,
  onLogoutComplete,
}: LogoutButtonProps) {
  const { logout, user } = useAuth();
  const { t, isRTL } = useI18n();
  const [showModal, setShowModal] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const handleLogout = async () => {
    if (showConfirmation && !showModal) {
      setShowModal(true);
      return;
    }

    try {
      setIsLoggingOut(true);
      onLogoutStart?.();
      
      // Add a small delay for better UX
      await new Promise(resolve => setTimeout(resolve, 300));
      
      logout();
      onLogoutComplete?.();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setIsLoggingOut(false);
      setShowModal(false);
    }
  };

  const handleCancel = () => {
    setShowModal(false);
  };

  const defaultClassName = `
    inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md
    text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2
    focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200
  `;

  return (
    <>
      <button
        onClick={handleLogout}
        disabled={isLoggingOut}
        className={className || defaultClassName}
        aria-label={`${t('common.logout')} ${user?.username || t('auth.currentUser')}`}
      >
        {isLoggingOut ? (
          <div className={`flex items-center ${isRTL ? 'flex-row-reverse' : 'flex-row'}`} style={{ fontFamily: isRTL ? 'Cairo, sans-serif' : 'inherit' }}>
            <svg
              className={`animate-spin h-4 w-4 text-white ${isRTL ? 'ml-2' : 'mr-2'}`}
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            {t('auth.signingOut')}
          </div>
        ) : (
          children || (
            <div className={`flex items-center ${isRTL ? 'flex-row-reverse' : 'flex-row'}`} style={{ fontFamily: isRTL ? 'Cairo, sans-serif' : 'inherit' }}>
              <svg
                className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                />
              </svg>
              {t('common.logout')}
            </div>
          )
        )}
      </button>

      {/* Confirmation Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true" dir={isRTL ? 'rtl' : 'ltr'}>
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div 
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" 
              aria-hidden="true"
              onClick={handleCancel}
            ></div>

            {/* Modal panel */}
            <div className="inline-block align-bottom bg-white rounded-lg overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full" style={{ textAlign: isRTL ? 'right' : 'left' }}>
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className={`sm:flex sm:items-start ${isRTL ? 'sm:flex-row-reverse' : ''}`}>
                  <div className={`mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10 ${isRTL ? 'sm:ml-4' : 'sm:mr-4'}`}>
                    <svg
                      className="h-6 w-6 text-red-600"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                      />
                    </svg>
                  </div>
                  <div className={`mt-3 text-center sm:mt-0 ${isRTL ? 'sm:mr-4 sm:text-right' : 'sm:ml-4 sm:text-left'}`}>
                    <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title" style={{ fontFamily: isRTL ? 'Cairo, sans-serif' : 'inherit' }}>
                      {t('auth.confirmLogout')}
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500" style={{ fontFamily: isRTL ? 'Cairo, sans-serif' : 'inherit' }}>
                        {t('auth.logoutConfirmMessage')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className={`bg-gray-50 px-4 py-3 sm:px-6 sm:flex ${isRTL ? 'sm:flex-row' : 'sm:flex-row-reverse'}`}>
                <button
                  type="button"
                  onClick={handleLogout}
                  className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:w-auto sm:text-sm ${isRTL ? 'sm:mr-3' : 'sm:ml-3'}`}
                  style={{ fontFamily: isRTL ? 'Cairo, sans-serif' : 'inherit' }}
                >
                  {t('common.logout')}
                </button>
                <button
                  type="button"
                  onClick={handleCancel}
                  className={`mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm ${isRTL ? 'sm:mr-3' : 'sm:ml-3'}`}
                  style={{ fontFamily: isRTL ? 'Cairo, sans-serif' : 'inherit' }}
                >
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default LogoutButton;